// Admin → Requests: people asking for an account on a closed server.
import { Check } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { relative } from '../../lib/format';
import type { AdminAccessRequest } from '../../lib/types';
import { useParent } from '../ParentContext';
import { LinkResult } from './shared';

// --- Access requests -------------------------------------------------------------------

export function AccessRequests({ items, reload }: { items: AdminAccessRequest[] | null; reload: () => void }) {
  if (!items) return <p className="muted">Loading…</p>;
  const pending = items.filter((r) => r.status === 'pending');
  const handled = items.filter((r) => r.status !== 'pending');
  return (
    <div className="stack">
      <p className="muted small">
        When sign-ups are closed, people asking for an account show up here. Approving one emails them an invite link to set a password.
      </p>
      {!items.length && <p className="muted">No access requests yet.</p>}
      {pending.map((r) => (
        <RequestCard key={r.id} req={r} reload={reload} />
      ))}
      {!!handled.length && <div className="field-label">Handled</div>}
      {handled.map((r) => (
        <RequestCard key={r.id} req={r} reload={reload} />
      ))}
    </div>
  );
}
function RequestCard({ req, reload }: { req: AdminAccessRequest; reload: () => void }) {
  const { toast } = useParent();
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [invite, setInvite] = useState<{ url: string; emailed: boolean } | null>(null);

  async function approve() {
    setBusy(true);
    try {
      const r = await api.post<{ inviteUrl: string; emailed: boolean }>(`/api/admin/access-requests/${req.id}/approve`);
      setInvite({ url: r.inviteUrl, emailed: r.emailed });
      toast({ text: r.emailed ? `Approved — invite emailed to ${req.email}.` : 'Approved — send them the invite link.', tone: 'good' });
      reload();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    setBusy(true);
    try {
      await api.post(`/api/admin/access-requests/${req.id}/decline`, { reason: reason.trim() || undefined });
      toast({ text: 'Request declined.', tone: 'good' });
      setDeclining(false);
      reload();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack">
      <div className="row-between">
        <div className="grow">
          <strong>{req.name}</strong>
          {req.status === 'approved' && <span className="tag good">Approved</span>}
          {req.status === 'declined' && <span className="tag bug">Declined</span>}
          <div className="muted small">
            {req.email} · {relative(req.createdAt)}
            {req.status !== 'pending' && req.decidedBy && <> · by {req.decidedBy}</>}
          </div>
        </div>
        {req.status === 'pending' && !invite && (
          <div className="actions">
            <button className="btn small primary" onClick={approve} disabled={busy}>
              <Check size={15} /> Approve
            </button>
            <button className="btn small danger-text" onClick={() => setDeclining((d) => !d)} disabled={busy}>
              Decline
            </button>
          </div>
        )}
      </div>
      {req.note && <p className="muted small">“{req.note}”</p>}
      {req.status === 'declined' && req.reason && <p className="muted small">Reason: {req.reason}</p>}
      {invite && <LinkResult url={invite.url} emailed={invite.emailed} to={req.email} what="invite link" />}
      {declining && (
        <div className="row-between">
          <input
            className="grow"
            placeholder="Reason (optional, not shown to them)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Decline reason"
          />
          <button className="btn small danger-text" onClick={decline} disabled={busy}>
            Decline request
          </button>
        </div>
      )}
    </section>
  );
}
